import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';

const ThreadHistory = ({ isOpen, onClose, darkMode, user, onSelectThread }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [threads, setThreads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filteredThreads, setFilteredThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef(null);
  const renameInputRef = useRef(null);

  // Fetch threads when the component opens and user is available
  useEffect(() => {
    if (isOpen && user) {
      fetchThreads();
    }
  }, [isOpen, user]);

  // Filter threads based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredThreads(threads);
    } else {
      const searchTermLower = searchQuery.toLowerCase();
      const filtered = threads.filter(thread => 
        (thread.customTitle || thread.title).toLowerCase().includes(searchTermLower)
      );
      setFilteredThreads(filtered);
    }
  }, [searchQuery, threads]);

  // Close context menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowContextMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus rename input when entering rename mode
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  // Fetch threads from the API
  const fetchThreads = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/threads`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching threads: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.threads) {
        setThreads(data.threads);
        setFilteredThreads(data.threads);
      } else {
        throw new Error(data.error || 'Failed to fetch threads');
      }
    } catch (err) {
      console.error('Thread fetch error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle thread selection
  const handleThreadSelect = async (threadId) => {
    if (!user) return;
    
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/thread/${threadId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching thread: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.thread) {
        onSelectThread(data.thread);
        onClose();
      } else {
        throw new Error(data.error || 'Failed to fetch thread');
      }
    } catch (err) {
      console.error('Thread selection error:', err);
      setError(err.message);
    }
  };

  // Handle thread options menu
  const handleThreadOptions = (e, thread) => {
    e.stopPropagation();
    setActiveThread(thread);
    setShowContextMenu(true);
  };

  // Handle thread deletion
  const handleDeleteThread = async (threadToDelete) => {
    if (!user) return;
    
    // Use the passed thread object or fall back to activeThread
    const targetThread = threadToDelete || activeThread;
    
    if (!targetThread) return;
    
    try {
      setIsDeleting(true);
      setError(null);
      
      const idToken = await user.getIdToken();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/thread/${targetThread.thread_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error deleting thread: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Update the threads list after successful deletion
        setThreads(threads.filter(t => t.thread_id !== targetThread.thread_id));
        // Reset active thread if it was the one deleted
        if (activeThread && activeThread.thread_id === targetThread.thread_id) {
          setActiveThread(null);
        }
        setShowContextMenu(false);
        // Show success message temporarily
        console.log('Thread deleted successfully');
      } else {
        throw new Error(data.error || 'Failed to delete thread');
      }
    } catch (err) {
      console.error('Thread deletion error:', err);
      setError(`Failed to delete thread: ${err.message}`);
    } finally {
      setIsDeleting(false);
      setShowContextMenu(false);
    }
  };

  // Start renaming thread
  const handleStartRename = () => {
    setIsRenaming(true);
    setNewTitle(activeThread.customTitle || activeThread.title);
    setShowContextMenu(false);
  };

  // Submit rename
  const handleRenameSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !activeThread || !newTitle.trim()) {
      setIsRenaming(false);
      return;
    }
    
    try {
      setError(null);
      
      const idToken = await user.getIdToken();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/thread/${activeThread.thread_id}/rename`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newTitle: newTitle.trim() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error renaming thread: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Update the threads list after successful rename
        setThreads(threads.map(t => 
          t.thread_id === activeThread.thread_id 
            ? { ...t, customTitle: newTitle.trim() } 
            : t
        ));
      } else {
        throw new Error(data.error || 'Failed to rename thread');
      }
    } catch (err) {
      console.error('Thread rename error:', err);
      setError(`Failed to rename thread: ${err.message}`);
    } finally {
      setIsRenaming(false);
    }
  };

  // Cancel rename
  const handleRenameCancel = () => {
    setIsRenaming(false);
  };

  // Handle search input
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Search is already handled by the useEffect
    }
  };

  // Format the date from timestamp
  const formatDate = (timestamp) => {
    return format(new Date(timestamp), 'MMM d, yyyy h:mm a');
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } flex flex-col`}
      >
        {/* Header */}
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Chat History</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b dark:border-gray-700">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearch}
              placeholder="Search threads..."
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400 dark:text-gray-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-grow">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <svg className="animate-spin h-6 w-6 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading threads...
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-500">
              Error: {error}
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No threads match your search' : 'No chat history found'}
            </div>
          ) : (
            <div className="divide-y dark:divide-gray-700">
              {filteredThreads.map((thread) => (
                <div 
                  key={thread.thread_id}
                  className={`relative p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors group ${activeThread && activeThread.thread_id === thread.thread_id ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                  onClick={() => {
                    setActiveThread(thread);
                    handleThreadSelect(thread.thread_id);
                  }}
                >
                  {isRenaming && activeThread && activeThread.thread_id === thread.thread_id ? (
                    <form onSubmit={handleRenameSubmit} className="mb-1">
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        className="w-full px-2 py-1 text-sm bg-white dark:bg-gray-700 border border-blue-400 dark:border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200"
                        onBlur={handleRenameCancel}
                        onKeyDown={(e) => e.key === 'Escape' && handleRenameCancel()}
                      />
                      <div className="flex justify-end mt-1 space-x-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenameCancel();
                          }}
                          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenameSubmit(e);
                          }}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="pr-14">
                      <h3 className="font-medium text-gray-800 dark:text-gray-200 mb-1 truncate">
                        {thread.customTitle || thread.title}
                      </h3>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                    <span>{formatDate(thread.timestamp)}</span>
                    <span>{thread.message_count} messages</span>
                  </div>
                  
                  {/* Thread Actions - Only visible on hover */}
                  <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 flex space-x-1 bg-white dark:bg-gray-800 rounded p-0.5 z-10">
                    <button
                      className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveThread(thread);
                        handleStartRename();
                      }}
                      title="Rename thread"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Pass the thread directly to the delete function instead of setting active thread first
                        handleDeleteThread(thread);
                      }}
                      disabled={isDeleting}
                      title="Delete thread"
                    >
                      {isDeleting && activeThread && activeThread.thread_id === thread.thread_id ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ThreadHistory; 